import React, { useState } from 'react';
import axios from 'axios';
import { Hotel, MapPin, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import SEO, { breadcrumbSchema } from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Hotels = () => {
  const [searchData, setSearchData] = useState({
    destination: '',
    check_in: '',
    check_out: '',
    rooms: '1',
    guests: '2'
  });
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);

  const featuredHotels = [
    { name: 'Atlantis The Palm', location: 'Dubai, UAE', rating: 5, price: '\u20b925,000/night', image: 'https://images.pexels.com/photos/18341554/pexels-photo-18341554.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
    { name: 'Bali Beach Resort', location: 'Bali, Indonesia', rating: 4, price: '\u20b912,000/night', image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxCYWxpJTIwdGVtcGxlfGVufDB8fHx8MTc4MDIzNzQyN3ww&ixlib=rb-4.1.0&q=85' },
    { name: 'Phuket Paradise', location: 'Phuket, Thailand', rating: 5, price: '\u20b915,000/night', image: 'https://images.pexels.com/photos/19595559/pexels-photo-19595559.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
    { name: 'Kashmir Valley Hotel', location: 'Srinagar, India', rating: 4, price: '\u20b98,500/night', image: 'https://images.unsplash.com/photo-1715457573748-8e8a70b2c1be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxLYXNobWlyJTIwRGFsJTIwTGFrZXxlbnwwfHx8fDE3ODAyMzc0Mjd8MA&ixlib=rb-4.1.0&q=85' },
    { name: 'Vietnam Bay Resort', location: 'Halong, Vietnam', rating: 5, price: '\u20b918,000/night', image: 'https://images.pexels.com/photos/35154841/pexels-photo-35154841.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
    { name: 'Singapore Marina', location: 'Singapore', rating: 5, price: '\u20b922,000/night', image: 'https://images.pexels.com/photos/18341554/pexels-photo-18341554.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' }
  ];

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error('Please fill in your contact details');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        inquiry_type: 'hotel',
        subject: `Hotel Inquiry: ${searchData.destination}`,
        message: `${searchData.rooms} room(s) for ${searchData.guests} guest(s)`,
        details: searchData
      });
      toast.success('Hotel inquiry submitted! We will share the best options shortly.');
      setSearchData({ destination: '', check_in: '', check_out: '', rooms: '1', guests: '2' });
      setFormData({ name: '', email: '', phone: '' });
    } catch (err) {
      toast.error('Failed to submit. Please call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Hotel Booking - Best Rates on Domestic & International Hotels"
        description="Book hotels worldwide with Varni Visa. Best deals on Dubai, Bali, Thailand, Kashmir, Singapore hotels. Luxury and budget options. Free quote in 30 mins."
        keywords="Hotel Booking, Hotel Booking Surat, International Hotel Booking, Dubai Hotels, Bali Hotels"
        path="/hotels"
        schema={breadcrumbSchema([{name: 'Home', path: '/'}, {name: 'Hotel Booking', path: '/hotels'}])}
      />
      <section 
        className="relative h-[350px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(45, 141, 180, 0.85), rgba(247, 148, 93, 0.75)), url('https://images.pexels.com/photos/7079773/pexels-photo-7079773.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Hotel Booking</h1>
          <p className="text-xl">Find your perfect stay at the best prices</p>
        </div>
      </section>

      <section className="py-12 -mt-20 relative z-10">
        <div className="container mx-auto px-4">
          <Card className="max-w-5xl mx-auto shadow-2xl">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Hotel className="text-[#2D8DB4]" />
                Search Hotels
              </h2>
              <form onSubmit={handleSearch} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="destination">Destination *</Label>
                    <Input
                      id="destination"
                      placeholder="City or hotel name"
                      required
                      value={searchData.destination}
                      onChange={(e) => setSearchData({ ...searchData, destination: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="checkin">Check-in *</Label>
                    <Input
                      id="checkin"
                      type="date"
                      required
                      value={searchData.check_in}
                      onChange={(e) => setSearchData({ ...searchData, check_in: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="checkout">Check-out *</Label>
                    <Input
                      id="checkout"
                      type="date"
                      required
                      value={searchData.check_out}
                      onChange={(e) => setSearchData({ ...searchData, check_out: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="rooms">Rooms</Label>
                    <Select value={searchData.rooms} onValueChange={(v) => setSearchData({ ...searchData, rooms: v })}>
                      <SelectTrigger id="rooms"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4].map(n => <SelectItem key={n} value={String(n)}>{n} Room{n > 1 ? 's' : ''}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="guests">Guests</Label>
                    <Select value={searchData.guests} onValueChange={(v) => setSearchData({ ...searchData, guests: v })}>
                      <SelectTrigger id="guests"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5,6,7,8].map(n => <SelectItem key={n} value={String(n)}>{n} Guest{n > 1 ? 's' : ''}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Your Contact Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="cname">Name *</Label>
                      <Input id="cname" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                    </div>
                    <div>
                      <Label htmlFor="cemail">Email</Label>
                      <Input id="cemail" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                    </div>
                    <div>
                      <Label htmlFor="cphone">Phone *</Label>
                      <Input id="cphone" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                    </div>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-[#2D8DB4] hover:bg-[#247a9a]" size="lg" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Get Best Hotel Deals'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Featured Hotels</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredHotels.map((hotel, idx) => (
              <Card key={idx} className="overflow-hidden hover:shadow-xl transition-all">
                <div className="h-48 overflow-hidden">
                  <img src={hotel.image} alt={hotel.name} className="w-full h-full object-cover hover:scale-110 transition-transform" />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-1">{hotel.name}</h3>
                  <p className="text-gray-500 text-sm flex items-center gap-1 mb-2">
                    <MapPin size={14} />{hotel.location}
                  </p>
                  <div className="flex gap-1 mb-3">
                    {[...Array(hotel.rating)].map((_, i) => (
                      <Star key={i} size={16} className="fill-[#F7945D] text-[#F7945D]" />
                    ))}
                  </div>
                  <p className="text-2xl font-bold text-[#F7945D]">{hotel.price}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Hotels;
